import { CircularProgress, CssBaseline, ThemeProvider } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import {
  Routes,
  Route,
  BrowserRouter,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import NiceModal from '@ebay/nice-modal-react';
import { SnackbarProvider } from 'notistack';
import { Suspense } from 'react';
import { PostHogProvider } from 'posthog-js/react';
import { routes as appRoutes } from './routes/app';
import { routes as landingRoutes } from './routes/landing';
import { routes as loginRoutes } from './routes/logins';
import MiniDrawer from './components/Drawer/MiniDrawer';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { RootState, store } from './store/store';
import PublicLayout from './layouts/PublicLayout';
import NotFound from './pages/NotFound';

const ProtectedRoute = ({
  adminOnly = false,
  children,
}: {
  adminOnly?: boolean;
  children: JSX.Element;
}) => {
  const { isAuthenticated } = useAuth();
  const loading = useSelector((state: RootState) => state.auth.loading);
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const location = useLocation();

  if (loading) {
    return <CircularProgress />;
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (adminOnly && currentUser?.type !== 'ADMIN') {
    return <Navigate to="/" replace />;
  }
  return children;
};

const theme = createTheme({
  components: {
    MuiDialogContent: {
      styleOverrides: { root: { paddingTop: '4px !important' } },
    },
  },
  typography: {
    fontFamily: '"IBM Plex Sans", system-ui, -apple-system, sans-serif',
    fontSize: 16,
    h1: {
      fontFamily: '"Bricolage Grotesque", "IBM Plex Sans", sans-serif',
      fontWeight: 800,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: '"Bricolage Grotesque", "IBM Plex Sans", sans-serif',
      fontWeight: 800,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontFamily: '"Bricolage Grotesque", "IBM Plex Sans", sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.015em',
    },
    h4: {
      fontFamily: '"Bricolage Grotesque", "IBM Plex Sans", sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.015em',
    },
    h5: {
      fontFamily: '"Bricolage Grotesque", "IBM Plex Sans", sans-serif',
      fontWeight: 700,
    },
    h6: {
      fontFamily: '"Bricolage Grotesque", "IBM Plex Sans", sans-serif',
      fontWeight: 700,
    },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  palette: {
    primary: {
      light: '#63b8ff',
      main: '#1F7CFF',
      dark: '#005db0',
      contrastText: '#fff',
    },
    secondary: {
      main: '#1f2421',
      light: '#44577f',
      dark: '#122c5a',
      contrastText: '#fff',
    },
    success: { main: '#27ae60', contrastText: '#fff' },
    highlight: {
      main: '#f39c12',
      light: '#f5b041',
      dark: '#c87f0a',
      contrastText: '#000',
    },
  },
});

const POSTHOG_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY as
  | string
  | undefined;

const Analytics = ({ children }: { children: JSX.Element }) =>
  POSTHOG_KEY ? (
    <PostHogProvider
      apiKey={POSTHOG_KEY}
      options={{ api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST }}
    >
      {children}
    </PostHogProvider>
  ) : (
    children
  );

export function App() {
  return (
    <ThemeProvider theme={theme}>
      <Provider store={store}>
        <Analytics>
          <BrowserRouter>
            <CssBaseline />
            <SnackbarProvider>
              <AuthProvider>
                <Suspense fallback={<CircularProgress />}>
                  <Routes>
                    <Route>
                      {loginRoutes.map((route) => (
                        <Route
                          key={route.key}
                          path={route.path}
                          element={<route.component />}
                        />
                      ))}
                    </Route>
                    <Route element={<PublicLayout />}>
                      {landingRoutes.map((route) => (
                        <Route
                          key={route.key}
                          path={route.path}
                          element={<route.component />}
                        />
                      ))}
                    </Route>
                    <Route
                      element={
                        <NiceModal.Provider>
                          <MiniDrawer />
                        </NiceModal.Provider>
                      }
                    >
                      {appRoutes.map((route) => (
                        <Route
                          key={route.key}
                          path={route.path}
                          element={
                            route.protected ? (
                              <ProtectedRoute adminOnly={route.adminOnly}>
                                <route.component />
                              </ProtectedRoute>
                            ) : (
                              <route.component />
                            )
                          }
                        />
                      ))}
                    </Route>
                    <Route path="/404" element={<NotFound />} />
                    <Route path="*" element={<Navigate to="/404" replace />} />
                  </Routes>
                </Suspense>
              </AuthProvider>
            </SnackbarProvider>
          </BrowserRouter>
        </Analytics>
      </Provider>
    </ThemeProvider>
  );
}

export function WrappedApp() {
  return <App />;
}
